import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { usePageTitle } from "@/lib/seo";
import { Reveal } from "@/components/Reveal";
import { CTASection } from "@/components/CTASection";
import { API } from "@/lib/api";

export default function Blog() {
  const { t, lang } = useLanguage();
  usePageTitle(t.meta.blog);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios
      .get(`${API}/articles`, { params: { lang } })
      .then((r) => setArticles(r.data))
      .catch(() => setArticles([]))
      .finally(() => setLoading(false));
  }, [lang]);

  return (
    <div data-testid="blog-page" className="pt-24">
      <section className="mx-auto max-w-7xl px-6 pb-16 pt-16 md:px-10 md:pt-24">
        <Reveal>
          <p className="flex items-center gap-3 text-xs uppercase tracking-[0.25em] text-bronze md:text-sm">
            <span className="h-px w-10 bg-bronze" />
            {t.blog.overline}
          </p>
          <h1 className="mt-8 max-w-4xl font-serif text-5xl leading-[0.95] tracking-tight text-ink md:text-6xl lg:text-7xl">
            {t.blog.heading}
          </h1>
          <p className="mt-8 max-w-2xl text-base leading-relaxed text-stone md:text-lg">{t.blog.sub}</p>
        </Reveal>
      </section>

      <section className="mx-auto max-w-7xl px-6 pb-24 md:px-10">
        {loading ? (
          <div className="grid gap-10 md:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3].map((n) => (
              <div key={n} className="animate-pulse">
                <div className="h-56 bg-sand" />
                <div className="mt-5 h-6 w-3/4 bg-sand" />
                <div className="mt-3 h-4 w-full bg-sand" />
              </div>
            ))}
          </div>
        ) : articles.length === 0 ? (
          <p data-testid="blog-empty-state" className="text-stone">{t.blog.empty}</p>
        ) : (
          <div className="grid gap-x-8 gap-y-14 md:grid-cols-2 lg:grid-cols-3">
            {articles.map((a, i) => (
              <Reveal key={a.slug} delay={(i % 3) * 0.08}>
                <Link data-testid={`blog-card-${a.slug}`} to={`/blog/${a.slug}`} className="group flex h-full flex-col gap-5">
                  <div className="relative h-60 overflow-hidden">
                    <img
                      src={a.image}
                      alt={a.title}
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <span className="absolute left-4 top-4 bg-ivory/85 px-3 py-1 text-[10px] uppercase tracking-widest text-bronze backdrop-blur">
                      {a.category}
                    </span>
                  </div>
                  <div className="flex flex-1 flex-col gap-3">
                    <p className="text-xs text-stone/60">{a.date} · {a.read_minutes} {t.blogSection.minRead}</p>
                    <h2 className="font-serif text-2xl leading-tight text-ink transition-colors duration-300 group-hover:text-bronze md:text-3xl">
                      {a.title}
                    </h2>
                    <p className="line-clamp-3 flex-1 text-sm leading-relaxed text-stone">{a.excerpt}</p>
                    <span className="flex items-center gap-2 text-xs font-medium uppercase tracking-widest text-bronze">
                      {t.blogSection.read}
                      <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        )}
      </section>

      <CTASection />
    </div>
  );
}
